import { useCallback, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { atom, useAtom } from 'jotai';

import { Heading, Button, RadioGroup, Modal, ModalContent } from '@restworld/ui-ds-base';

import { useUpdateResearchStatusForm } from './hooks/use-update-research-status-form';

export const useStatusTranslatedOptions = () => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        status_key: 'worker_research_status_looking_urgently',
        label: t('onboarding-page:available_immediately'),
      },
      {
        status_key: 'worker_research_status_looking_without_hurry',
        label: t('onboarding-page:looking_for_work_without_hesitation'),
      },
      {
        status_key: 'worker_research_status_employed_looking_for_change',
        label: t('onboarding-page:i_work_but_m_available'),
      },
      {
        status_key: 'worker_research_status_not_searching',
        label: t('onboarding-page:m_not_looking_for_work'),
      },
    ],
    [t]
  );
};

export const showUpdateStatusModalAtom = atom<boolean>(false);
export const UpdateResearchStatusModal = ({ showUpdateStatusButton = true }: { showUpdateStatusButton?: boolean }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useAtom(showUpdateStatusModalAtom);

  const onClose = useCallback(() => {
    setShowModal(false);
  }, [setShowModal]);

  const openModal = useCallback(() => {
    setShowModal(true);
  }, [setShowModal]);

  const statusOptions = useStatusTranslatedOptions();

  const useMemoizedValues = useMemo(() => {
    const researchStatusOptions = statusOptions.map((status) => ({
      id: status.status_key,
      key: status.status_key,
      label: status.label,
    }));
    return {
      researchStatusOptions,
    };
  }, [statusOptions]);

  const { researchStatusOptions } = useMemoizedValues;
  const { control, handleSubmit, onSubmit, loading } = useUpdateResearchStatusForm({ onClose });

  return (
    <>
      {showUpdateStatusButton && (
        <Button variant="primary" className={'flex-1 h-13'} onClick={openModal}>
          {t('onboarding-page:update_status')}
        </Button>
      )}
      <Modal open={showModal} onOpenChange={(v) => !v && onClose()}>
        <ModalContent variant="center" className="lg:max-w-sm bg-gray-50">
          <form className="flex flex-col items-center w-full gap-4" onSubmit={handleSubmit(onSubmit)}>
            <Heading>{t('onboarding-page:your_employment_status')}</Heading>
            <Controller
              name="researchStatus"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <RadioGroup
                  options={researchStatusOptions}
                  id="research-status"
                  value={field.value}
                  onChange={field.onChange}
                  isFullWidth
                />
              )}
            />
            <div className="flex flex-col items-center w-full gap-4 pb-2">
              <Button type="submit" loading={loading} fullwidth>
                {t('common:continue')}
              </Button>
              <Button type="button" variant="secondary-neutral" fullwidth onClick={onClose}>
                {t('common:cancel')}
              </Button>
            </div>
          </form>
        </ModalContent>
      </Modal>
    </>
  );
};
